import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Crosshair, Loader2, MapPin, MapPinned, Star, X } from 'lucide-react';
import type { PlaceInput as Place } from '@tcrd/shared';
import { cn } from '@tcrd/ui';
import { api, errorMessage } from '../lib/api';
import { currentPosition } from '../lib/location';

interface Suggestion {
  placeId: string;
  primary: string;
  secondary: string;
}
interface Favorite {
  id: string;
  label: string;
  address: string;
  lat: number;
  lng: number;
  placeId: string | null;
}

/**
 * Campo de dirección con autocompletado (vía API), ubicación actual,
 * direcciones favoritas y selección en el mapa.
 */
export function PlaceInput({
  label,
  value,
  onChange,
  onPickOnMap,
  dotClass = 'bg-brand-black',
  allowCurrentLocation = true,
  error,
}: {
  label: string;
  value: Place | null;
  onChange: (p: Place | null) => void;
  onPickOnMap?: () => void;
  dotClass?: string;
  allowCurrentLocation?: boolean;
  error?: string;
}) {
  const id = useId();
  const [text, setText] = useState(value?.address ?? '');
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const session = useRef(crypto.randomUUID());
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => setText(value?.address ?? ''), [value?.address]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 350);
    return () => clearTimeout(t);
  }, [text]);
  useEffect(() => {
    const close = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const searching = open && debounced.length >= 3 && debounced !== value?.address;
  const suggestions = useQuery({
    queryKey: ['autocomplete', debounced],
    queryFn: () => api<Suggestion[]>('/routes/autocomplete', { query: { q: debounced, session: session.current } }),
    enabled: searching,
    staleTime: 60_000,
  });
  const favorites = useQuery({ queryKey: ['addresses'], queryFn: () => api<Favorite[]>('/customers/me/addresses'), staleTime: 300_000 });

  const choose = async (s: Suggestion) => {
    setBusy(true);
    setLocalError(null);
    try {
      const d = await api<{ placeId: string | null; address: string; lat: number; lng: number }>(`/routes/place/${encodeURIComponent(s.placeId)}`, { query: { session: session.current } });
      onChange({ address: d.address || `${s.primary}, ${s.secondary}`, lat: d.lat, lng: d.lng, placeId: d.placeId ?? undefined });
      session.current = crypto.randomUUID(); // la sesión de Places termina al elegir
      setOpen(false);
    } catch (e) {
      setLocalError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const useMine = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      const pos = await currentPosition();
      const d = await api<{ placeId: string | null; address: string; lat: number; lng: number }>('/routes/reverse', { query: { lat: pos.lat, lng: pos.lng } });
      onChange({ address: d.address, lat: pos.lat, lng: pos.lng, placeId: d.placeId ?? undefined });
      setOpen(false);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const shownError = error ?? localError;
  return (
    <div ref={wrap} className="relative">
      <label htmlFor={id} className="sr-only">{label}</label>
      <div className={cn('flex items-center gap-3 rounded-2xl border bg-[var(--surface)] px-4 h-14', shownError ? 'border-brand-red' : 'border-[var(--border)]', open && 'ring-2 ring-brand-red/50 border-transparent')}>
        <span className={cn('size-3 rounded-full shrink-0', dotClass)} aria-hidden />
        <input
          id={id}
          value={text}
          placeholder={label}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            if (value) onChange(null);
          }}
          className="flex-1 min-w-0 bg-transparent outline-none text-[15px] placeholder:text-[var(--text-muted)]"
        />
        {busy && <Loader2 className="size-4 animate-spin text-[var(--text-muted)]" aria-hidden />}
        {text && !busy && (
          <button type="button" className="tap -mr-3 flex items-center justify-center text-[var(--text-muted)]" aria-label={`Borrar ${label}`} onClick={() => (setText(''), onChange(null), setOpen(true))}>
            <X className="size-4" />
          </button>
        )}
      </div>
      {shownError && <p role="alert" className="mt-1 text-xs text-brand-red">{shownError}</p>}

      {open && (
        <ul id={`${id}-list`} role="listbox" className="absolute z-40 mt-2 w-full max-h-80 overflow-auto rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-float)] py-1">
          {allowCurrentLocation && (
            <li>
              <button type="button" onClick={useMine} className="tap w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-black/5 dark:hover:bg-white/5">
                <Crosshair className="size-5 text-sky-600" aria-hidden /> <span className="font-medium">Usar mi ubicación actual</span>
              </button>
            </li>
          )}
          {onPickOnMap && (
            <li>
              <button type="button" onClick={() => (setOpen(false), onPickOnMap())} className="tap w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-black/5 dark:hover:bg-white/5">
                <MapPinned className="size-5 text-[var(--text-muted)]" aria-hidden /> <span className="font-medium">Elegir en el mapa</span>
              </button>
            </li>
          )}
          {!searching &&
            favorites.data?.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => (onChange({ address: f.address, lat: f.lat, lng: f.lng, placeId: f.placeId ?? undefined }), setOpen(false))}
                  className="tap w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <Star className="size-5 text-amber-500" aria-hidden />
                  <span className="min-w-0">
                    <span className="block font-medium">{f.label}</span>
                    <span className="block text-xs text-[var(--text-muted)] truncate">{f.address}</span>
                  </span>
                </button>
              </li>
            ))}
          {searching && suggestions.isFetching && <li className="px-4 py-3 text-sm text-[var(--text-muted)]">Buscando…</li>}
          {searching && suggestions.data?.length === 0 && !suggestions.isFetching && <li className="px-4 py-3 text-sm text-[var(--text-muted)]">Sin resultados. Prueba con otra referencia o elige en el mapa.</li>}
          {searching &&
            suggestions.data?.map((s) => (
              <li key={s.placeId}>
                <button type="button" onClick={() => choose(s)} className="tap w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-black/5 dark:hover:bg-white/5">
                  <MapPin className="size-5 text-[var(--text-muted)] shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="block font-medium truncate">{s.primary}</span>
                    <span className="block text-xs text-[var(--text-muted)] truncate">{s.secondary}</span>
                  </span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
